<template>
  <div class="admin-page" v-loading="loading">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>角色权限</h1>
        <p>决定每个角色登录后能看见哪些菜单，保存后对方刷新即生效</p>
      </div>
      <div class="head-actions">
        <span class="role-lock"><span class="dot" />只有超级管理员可以修改</span>
      </div>
    </div>

    <!-- ── 角色切换 ───────────────────────────────────────── -->
    <div class="role-tabs">
      <div
        v-for="r in roles"
        :key="r.role"
        class="role-tab"
        :class="{ 'is-active': activeRole === r.role }"
        @click="activeRole = r.role"
      >
        <span class="role-tab__name">{{ r.label }}</span>
        <span class="role-tab__count">
          {{ r.role === 'super' ? '全部' : r.menuKeys.length + ' 项' }}
        </span>
      </div>
    </div>

    <div v-for="r in roles" :key="r.role" v-show="activeRole === r.role" class="panel role-panel">
      <div class="panel-head">
        <div class="panel-head__left">
          <div class="panel-head__title">{{ r.label }}</div>
          <div class="panel-head__desc">{{ roleDesc(r.role) }}</div>
        </div>
        <el-button
          v-if="r.editable"
          type="text"
          icon="el-icon-refresh-left"
          @click="resetRole(r)"
        >恢复默认</el-button>
      </div>

      <div v-if="!r.editable" class="locked">
        <i class="el-icon-lock"></i>
        超级管理员固定拥有全部菜单，不需要配置。
      </div>

      <template v-else>
        <div class="groups">
          <div v-for="group in groupedMenus" :key="group.name" class="group">
            <div class="group__head">
              <el-checkbox
                :value="isGroupAllChecked(r, group)"
                :indeterminate="isGroupIndeterminate(r, group)"
                @change="toggleGroup(r, group, $event)"
              >
                <i v-if="group.icon" :class="group.icon" class="group__icon"></i>
                {{ group.name }}
              </el-checkbox>
              <span class="group__meta">{{ countChecked(r, group) }} / {{ group.items.length }}</span>
            </div>
            <el-checkbox-group v-model="r.menuKeys" class="group__body">
              <el-checkbox
                v-for="menu in group.items"
                :key="menu.key"
                :label="menu.key"
                :disabled="!canAssign(menu, r)"
              >
                <span class="menu-title">{{ menu.title }}</span>
                <span class="menu-key">{{ menu.key }}</span>
                <!--
                  ⛔ minRole 项对本角色置灰并给出说明，而不是直接隐藏。
                  隐藏会让人问"这个菜单怎么没了"；置灰 + 一句话解释才是可理解的。
                  superOnly 的项后端已从树里剔除，这里只会遇到 minRole。
                -->
                <span v-if="!canAssign(menu, r)" class="menu-lock">
                  <i class="el-icon-lock"></i>{{ minRoleHint(menu) }}
                </span>
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </div>

        <div class="panel-actions">
          <el-button class="ghost-btn" @click="fetchConfig">重置</el-button>
          <el-button type="primary" class="primary-btn" :loading="saving === r.role" @click="save(r)">保存{{ r.label }}的菜单</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getRoleMenuConfig, saveRoleMenu, resetRoleMenu } from '@/api/role'

/**
 * 角色层级，与后端 RoleConstant.level 保持一致。
 * ⛔ 只用于"置灰判断"这一个地方；任何真正的权限判定都在后端。
 *    两边数值必须一致，改角色体系时一起改（这是本文件唯一与后端耦合的地方）。
 */
function roleLevel(role) {
  if (role === 'super') {
    return 3
  }
  if (role === 'admin') {
    return 2
  }
  if (role === 'operator') {
    return 1
  }
  return 0
}

export default {
  name: 'RolePermission',
  data() {
    return {
      loading: false,
      saving: '',
      activeRole: 'admin',
      menus: [],
      roles: []
    }
  },
  computed: {
    /** 把扁平菜单按 group 折叠成卡片，界面上按"功能模块"勾选比一长条平铺好认 */
    groupedMenus() {
      const map = new Map()
      this.menus.forEach(menu => {
        const name = menu.group && menu.group.length ? menu.group : '基础'
        if (!map.has(name)) {
          map.set(name, { name, icon: menu.groupIcon, items: [] })
        }
        map.get(name).items.push(menu)
      })
      return Array.from(map.values())
    }
  },
  created() {
    this.fetchConfig()
  },
  methods: {
    fetchConfig() {
      this.loading = true
      getRoleMenuConfig().then(res => {
        const data = res.data || {}
        this.menus = data.menus || []
        // 深拷贝一份，取消/保存前不污染源数据
        this.roles = (data.roles || []).map(role => ({
          ...role,
          menuKeys: [...(role.menuKeys || [])]
        }))
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    roleDesc(role) {
      if (role === 'super') {
        return '平台方。全部菜单、全部学校的数据，也是唯一能改这份配置的角色。'
      }
      if (role === 'admin') {
        return '由超管授权。能看到所授权学校的数据，可以在自己范围内创建和授权运营。'
      }
      return '由超管或管理员添加。只做被授权学校范围内的日常内容处理，不涉及资金与系统配置。'
    },

    /**
     * 这个菜单能不能勾给这个角色。
     * -------------------------------------------------------------------
     * ⛔ 判据在后端（MenuConstant.visibleTo），前端只负责"置灰 + 解释"。
     *    前端置灰只是体验；绕过前端直接提交照样会被后端 canAssign 剔除。
     *    前后端用同一个规则（minRole / superOnly），才不会出现
     *    "界面能勾、保存后被悄悄丢掉"这种最让人困惑的情况。
     */
    canAssign(menu, role) {
      if (menu.superOnly) {
        return role.role === 'super'
      }
      if (!menu.minRole) {
        return true
      }
      return roleLevel(role.role) >= roleLevel(menu.minRole)
    },

    /** 置灰原因，一句话说清而不是让人猜 */
    minRoleHint(menu) {
      if (!menu.minRole) {
        return '不可分配'
      }
      const label = menu.minRole === 'admin' ? '管理员' : (menu.minRole === 'super' ? '超管' : menu.minRole)
      return '仅' + label + '及以上可见'
    },

    countChecked(role, group) {
      return group.items.filter(item => role.menuKeys.includes(item.key)).length
    },
    isGroupAllChecked(role, group) {
      return group.items.length > 0 && this.countChecked(role, group) === group.items.length
    },
    isGroupIndeterminate(role, group) {
      const count = this.countChecked(role, group)
      return count > 0 && count < group.items.length
    },
    toggleGroup(role, group, checked) {
      const keys = group.items.map(item => item.key)
      if (checked) {
        const merged = new Set([...role.menuKeys, ...keys])
        role.menuKeys = Array.from(merged)
      } else {
        role.menuKeys = role.menuKeys.filter(key => !keys.includes(key))
      }
    },
    save(role) {
      this.saving = role.role
      saveRoleMenu({ role: role.role, menuKeys: role.menuKeys }).then(() => {
        this.$message.success(role.label + '的菜单已保存')
        this.saving = ''
      }).catch(() => {
        this.saving = ''
      })
    },
    resetRole(role) {
      this.$confirm(`确定把「${role.label}」的菜单恢复成系统默认吗？`, '提示', {
        type: 'warning'
      }).then(() => {
        resetRoleMenu(role.role).then(() => {
          this.$message.success('已恢复默认')
          this.fetchConfig()
        })
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.role-lock {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: var(--ink-400);

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--warn);
    margin-right: 6px;
  }
}

.role-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.role-tab {
  flex: 1;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-lg);
  padding: 14px 18px;
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    border-color: var(--ink-300);
  }

  &.is-active {
    border-color: var(--rose-400);
    box-shadow: 0 0 0 3px var(--rose-50);
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink-900);
  }

  &__count {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.role-panel {
  padding: 20px 22px 8px;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--ink-100);

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink-900);
  }

  &__desc {
    font-size: 13px;
    color: var(--ink-400);
    margin-top: 4px;
    line-height: 1.6;
  }
}

.locked {
  padding: 28px 0;
  text-align: center;
  color: var(--ink-400);
  font-size: 13px;

  i {
    margin-right: 6px;
  }
}

.groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  padding: 16px 0 4px;
}

.group {
  border: 1px solid var(--ink-200);
  border-radius: var(--r-md);
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--paper);
    padding: 8px 14px;

    .group__icon {
      margin-right: 4px;
      color: var(--ink-400);
    }
  }

  &__meta {
    font-size: 12px;
    color: var(--ink-350);
  }

  &__body {
    padding: 10px 14px 6px;
    display: flex;
    flex-direction: column;

    ::v-deep .el-checkbox {
      margin: 0 0 8px 0;
      height: 22px;
      line-height: 22px;
    }
  }
}

.menu-title {
  color: var(--ink-900);
}

.menu-key {
  font-size: 11px;
  color: var(--ink-350);
  margin-left: 8px;
}

/* minRole 项对本角色不可勾：置灰 + 一句话说明，而不是直接隐藏 */
.menu-lock {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  font-size: 10px;
  color: var(--warn-text);
  background: var(--warn-bg);
  border-radius: 3px;
  padding: 1px 5px;

  i {
    font-size: 9px;
  }
}

.panel-actions {
  padding: 16px 0 12px;
  text-align: right;
  border-top: 1px solid var(--ink-100);
  margin-top: 8px;
}
</style>
